import {AfterViewInit, Component, forwardRef, Injector, Input, OnInit, ViewChild} from '@angular/core';
import {NgbDatepicker, NgbDateStruct, NgbPopover, NgbPopoverConfig, NgbTimeStruct} from '@ng-bootstrap/ng-bootstrap';
import {ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl} from '@angular/forms';
import {DatePipe} from '@angular/common';
import {DateTimeModel} from './date-time.model';
import {noop} from 'rxjs';

@Component({
    selector: 'app-date-time-picker',
    templateUrl: './date-time-picker.component.html',
    providers: [
        DatePipe,
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DateTimePickerComponent),
            multi: true
        }
    ]
})
export class DateTimePickerComponent implements ControlValueAccessor, OnInit, AfterViewInit {
    @Input()
    dateString: string;

    @Input()
    inputDatetimeFormat = 'dd/MM/yyyy HH:mm';
    @Input()
    hourStep = 1;
    @Input()
    minuteStep = 1;
    @Input()
    secondStep = 1;
    @Input()
    seconds = false;

    @Input()
    disabled = false;

    public showTimePickerToggle = false;

    public datetime: DateTimeModel = new DateTimeModel();
    public ngControl: NgControl;

    private firstTimeAssign = true;
    @ViewChild(NgbPopover, {static: true})
    private popover: NgbPopover;
    private onTouched: () => void = noop;
    private onChange: (_: any) => void = noop;

    constructor(private config: NgbPopoverConfig, private inj: Injector) {
        config.autoClose = 'outside';
        config.placement = 'auto';
    }

    inputBlur($event) {
        this.onTouched();
    }

    ngAfterViewInit(): void {
        this.popover.hidden.subscribe($event => {
            this.showTimePickerToggle = false;
        });
    }

    ngOnInit(): void {
        this.ngControl = this.inj.get(NgControl);
    }

    onDateChange($event: string | NgbDateStruct, dp: NgbDatepicker) {
        const date = new DateTimeModel($event);

        if (!date) {
            this.dateString = this.dateString;
            return;
        }

        if (!this.datetime) {
            this.datetime = date;
        }

        this.datetime.year = date.year;
        this.datetime.month = date.month;
        this.datetime.day = date.day;

        const adjustedDate = new Date(this.datetime.toString());
        if (this.datetime.timeZoneOffset !== adjustedDate.getTimezoneOffset()) {
            this.datetime.timeZoneOffset = adjustedDate.getTimezoneOffset();
        }

        this.setDateStringModel();
    }

    onInputChange($event: any) {
        const value = $event.target.value;
        const dt = DateTimeModel.fromLocalString(value);

        if (dt) {
            this.datetime = dt;
            this.setDateStringModel();
        } else if (value.trim() === '') {
            this.datetime = new DateTimeModel();
            this.dateString = '';
            this.onChange(this.dateString);
        } else {
            this.onChange(value);
        }
    }

    onTimeChange(event: NgbTimeStruct) {
        this.datetime.hour = event.hour;
        this.datetime.minute = event.minute;
        this.datetime.second = event.second;

        this.setDateStringModel();
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    writeValue(newModel: string) {
        if (newModel) {
            this.datetime = Object.assign(
                this.datetime,
                DateTimeModel.fromLocalString(newModel)
            );
            this.dateString = newModel;
            this.setDateStringModel();
        } else {
            this.datetime = new DateTimeModel();
        }
    }

    setDateStringModel() {
        this.dateString = this.datetime.toString();

        if (this.dateString !== null) {
            this.onChange(this.dateString);
        } else {
            // Skip very first assignment to null done by Angular
            if (this.dateString !== null) {
                this.firstTimeAssign = false;
            }
        }
    }

    toggleDateTimeState($event) {
        this.showTimePickerToggle = !this.showTimePickerToggle;
        $event.stopPropagation();
    }
}
